import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TVA_CHOICES = [0, 5.5, 10, 20];
const UNIT_CHOICES = ['u', 'h', 'ml', 'm2', 'm3', 'km', 'kg', 'sac', 'pot', 'rouleau', 'plaque', 'boite', 'palette', 'paire', 'litre'];
const EMPTY = { name: '', unit: 'u', price_ht: 0, tva_rate: 20, category: '', supplier: '', min_threshold: 0, coefficient_m2: 1, pack_size: 0, pack_sub_unit: '', consumption_per_m2: 0 };

// Common sub-units for conditionnement (1 unit of sale contains X sub-units).
const SUB_UNIT_CHOICES = ['kg', 'g', 'L', 'mL', 'm', 'ml', 'm2', 'm3', 'plaque', 'piece', 'vis', 'unite'];

/**
 * Reusable modal to create or edit a material in the catalog.
 * Used by MaterialsCatalogPage AND MaterialsStockPage. After save, calls
 * onSaved() with the API response so the parent can refresh / navigate.
 */
export const MaterialFormModal = ({ show, onClose, onSaved, editItem = null }) => {
  const [form, setForm] = useState(EMPTY);
  const [categoryList, setCategoryList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [creatingCat, setCreatingCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const editId = editItem?.id || null;

  useEffect(() => {
    if (!show) return;
    if (editItem) {
      setForm({
        name: editItem.name || '',
        unit: editItem.unit || 'u',
        price_ht: editItem.price_ht || 0,
        tva_rate: editItem.tva_rate ?? 20,
        category: editItem.category || '',
        supplier: editItem.supplier || '',
        min_threshold: editItem.min_threshold || 0,
        coefficient_m2: editItem.coefficient_m2 ?? 1,
        pack_size: editItem.pack_size || 0,
        pack_sub_unit: editItem.pack_sub_unit || '',
        consumption_per_m2: editItem.consumption_per_m2 || 0,
      });
    } else {
      setForm(EMPTY);
    }
    // Hydrate categories + suppliers from existing materials so the user can
    // re-use a value already entered (avoids duplicates / typos).
    axios.get(`${API_URL}/api/materials/catalog`, { withCredentials: true })
      .then((r) => {
        const cats = new Set();
        const sups = new Set();
        (r.data || []).forEach((m) => {
          if (m.category) cats.add(m.category);
          if (m.supplier) sups.add(m.supplier);
        });
        setCategoryList(Array.from(cats).sort((a, b) => a.localeCompare(b, 'fr')));
        setSupplierList(Array.from(sups).sort((a, b) => a.localeCompare(b, 'fr')));
      })
      .catch(() => {});
  }, [show, editItem]);

  if (!show) return null;

  const handleAddCategory = () => {
    const v = (newCatName || '').trim();
    if (!v) { setCreatingCat(false); return; }
    setCategoryList((prev) => prev.includes(v) ? prev : [...prev, v].sort((a, b) => a.localeCompare(b, 'fr')));
    setForm((f) => ({ ...f, category: v }));
    setNewCatName('');
    setCreatingCat(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      unit: form.unit,
      price_ht: parseFloat(form.price_ht || 0),
      tva_rate: parseFloat(form.tva_rate || 20),
      category: form.category.trim(),
      supplier: (form.supplier || '').trim(),
      min_threshold: parseFloat(form.min_threshold || 0),
      coefficient_m2: parseFloat(form.coefficient_m2 ?? 1),
      pack_size: parseFloat(form.pack_size || 0),
      pack_sub_unit: (form.pack_sub_unit || '').trim(),
      consumption_per_m2: parseFloat(form.consumption_per_m2 || 0),
    };
    if (!payload.name) { toast.error('Nom requis'); return; }
    try {
      let resp;
      if (editId) {
        resp = await axios.put(`${API_URL}/api/materials/catalog/${editId}`, payload, { withCredentials: true });
        toast.success('Materiau mis a jour');
      } else {
        resp = await axios.post(`${API_URL}/api/materials/catalog`, payload, { withCredentials: true });
        toast.success('Materiau ajoute au catalogue');
      }
      if (onSaved) onSaved(resp?.data || payload);
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '8px', width: '100%', maxWidth: '480px', padding: '22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>{editId ? 'Modifier' : 'Ajouter'} un matériau</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="material-modal-close"><X size={22} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input data-testid="material-name-input" className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Categorie</label>
              {!creatingCat ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <select
                    data-testid="material-category-select"
                    className="form-input"
                    value={form.category || ''}
                    onChange={(e) => {
                      if (e.target.value === '__new__') { setCreatingCat(true); return; }
                      setForm({ ...form, category: e.target.value });
                    }}
                    style={{ flex: 1 }}
                  >
                    <option value="">Aucune</option>
                    {form.category && !categoryList.includes(form.category) && (
                      <option value={form.category}>{form.category}</option>
                    )}
                    {categoryList.map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value="__new__">+ Nouvelle categorie...</option>
                  </select>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    data-testid="material-new-category-input"
                    className="form-input"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="Ex: Cloisons, Gros oeuvre..."
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleAddCategory}>OK</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setCreatingCat(false); setNewCatName(''); }}>X</button>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Unite</label>
              <select className="form-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                {UNIT_CHOICES.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Fournisseur</label>
            <input
              data-testid="material-supplier-input"
              className="form-input"
              value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              placeholder={supplierList.slice(0, 3).join(', ') || 'Ex: Point P, Saint-Gobain, BigMat...'}
              list="material-supplier-presets"
            />
            <datalist id="material-supplier-presets">
              {supplierList.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Prix HT (EUR) *</label>
              <input data-testid="material-price-input" type="number" min="0" step="0.01" className="form-input" value={form.price_ht} onChange={(e) => setForm({ ...form, price_ht: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Taux TVA (%) *</label>
              <input
                data-testid="material-tva-input"
                type="number"
                min="0"
                step="0.1"
                list="tva-presets"
                className="form-input"
                value={form.tva_rate}
                onChange={(e) => setForm({ ...form, tva_rate: e.target.value })}
              />
              <datalist id="tva-presets">
                {TVA_CHOICES.map((v) => <option key={v} value={v} />)}
              </datalist>
              <p style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: '4px' }}>Suggestions : 0, 5.5, 10, 20%</p>
            </div>
          </div>
          <div style={{ padding: '10px 12px', background: '#F4F5F7', borderRadius: '6px', marginBottom: '14px', fontSize: '0.85rem' }}>
            <strong>Prix TTC estime :</strong>{' '}
            {(parseFloat(form.price_ht || 0) * (1 + parseFloat(form.tva_rate || 20) / 100)).toFixed(2)} EUR
          </div>
          <div className="form-group">
            <label className="form-label">Seuil d'alerte de stock ({form.unit})</label>
            <input data-testid="material-threshold-input" type="number" min="0" step="1" className="form-input" value={form.min_threshold} onChange={(e) => setForm({ ...form, min_threshold: e.target.value })} placeholder="0 = pas d'alerte" />
            <p style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: '4px' }}>Les admins seront notifies des que le stock passe sous ce seuil.</p>
          </div>
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '12px', marginBottom: '12px' }}>
            <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>
              Conditionnement <span style={{ color: '#5E5F66', fontWeight: 400 }}>(optionnel — pour calcul auto du coefficient)</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 1fr', gap: 8, alignItems: 'end' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Quantite par {form.unit || 'unite'}</label>
                <input
                  data-testid="material-pack-size-input"
                  type="number"
                  min="0"
                  step="0.001"
                  className="form-input"
                  value={form.pack_size || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const pack = parseFloat(v || 0);
                    const cons = parseFloat(form.consumption_per_m2 || 0);
                    const newCoef = pack > 0 && cons > 0 ? Math.round((cons / pack) * 1000) / 1000 : form.coefficient_m2;
                    setForm({ ...form, pack_size: v, coefficient_m2: newCoef });
                  }}
                  placeholder="ex: 25"
                />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Sous-unite</label>
                <input
                  data-testid="material-pack-sub-unit-input"
                  className="form-input"
                  value={form.pack_sub_unit || ''}
                  onChange={(e) => setForm({ ...form, pack_sub_unit: e.target.value })}
                  placeholder="kg"
                  list="pack-sub-unit-list"
                />
                <datalist id="pack-sub-unit-list">
                  {SUB_UNIT_CHOICES.map((u) => <option key={u} value={u} />)}
                </datalist>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Conso /m² ({form.pack_sub_unit || 'sous-unite'})</label>
                <input
                  data-testid="material-consumption-input"
                  type="number"
                  min="0"
                  step="0.001"
                  className="form-input"
                  value={form.consumption_per_m2 || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const pack = parseFloat(form.pack_size || 0);
                    const cons = parseFloat(v || 0);
                    // Auto-recompute coefficient_m2 = consumption / pack_size (3 décimales)
                    const newCoef = pack > 0 && cons > 0 ? Math.round((cons / pack) * 1000) / 1000 : form.coefficient_m2;
                    setForm({ ...form, consumption_per_m2: v, coefficient_m2: newCoef });
                  }}
                  placeholder="ex: 3"
                />
              </div>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: 8, marginBottom: 0 }}>
              Ex: 1 sac de <strong>25 kg</strong>, conso <strong>3 kg/m²</strong> → coefficient = 3 / 25 = <strong>0,12 sac/m²</strong>.
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Coefficient par m² ({form.unit}/m²) <span style={{ color: '#5E5F66', fontWeight: 400, fontSize: '0.78rem' }}>(calculé auto si conditionnement renseigne)</span></label>
            <input
              data-testid="material-coef-m2-input"
              type="number"
              min="0"
              step="0.001"
              className="form-input"
              value={form.coefficient_m2}
              onChange={(e) => setForm({ ...form, coefficient_m2: e.target.value })}
              placeholder="1"
            />
            <p style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: '4px' }}>
              Quantité de ce matériau nécessaire pour 1 m² (ex: 0.333 plaque/m² pour Placo BA13). Appliqué automatiquement quand l'article est au m².
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" data-testid="save-material-btn">{editId ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialFormModal;
